import type { IntakeSeller } from '../api/endpoints';
import { StationStore } from '../helperStations/store';

/**
 * Lokale Daten der Warenannahme (U15c): Warteschlange der Scans ohne Netz und eine gespeicherte Kopie der Verkäuferliste.
 * Gleiche Technik wie die Stationen (`helperStations/*`), aber eine eigene Datenbank je Basar: Die Löschfristen sind andere
 * (Namen der Verkäufer auf dem Gerät), und die Rückgabe räumt ihre Daten nach eigenen Regeln auf.
 */
export const INTAKE_PREFIX = 'intake-';

export const intakeDbName = (eventId: string): string => `${INTAKE_PREFIX}${eventId}`;

/** So lange gilt die gespeicherte Verkäuferliste höchstens (Datensparsamkeit: Namen auf dem Gerät). */
export const SELLER_LIST_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const SELLERS_KEY = 'sellers';

/**
 * Zähler der Löschläufe (`clearIntakeCaches`). Wer eine Verkäuferliste lädt, merkt sich den Stand vor dem Laden und speichert
 * nur, wenn er unverändert ist: Eine Antwort, die erst nach dem Abmelden eintrifft, darf die Liste nicht wieder auf das Gerät
 * bringen.
 */
let generation = 0;
export const intakeGeneration = (): number => generation;
const REJECTED_KEY = 'rejected';

/** Gespeicherte Kopie der Verkäuferliste mit Stand. Sie gehört der Person, die sie geladen hat (Datenschutz, Regel 11). */
export interface SellerSnapshot {
  savedAt: number;
  userId: string | null;
  sellers: IntakeSeller[];
}

/** Ein gespeicherter Scan, den der Server abgelehnt hat (Code unbekannt, Artikel gelöscht, …): Der Hinweis bleibt, bis er gelesen ist. */
export interface RejectedScan {
  code: string;
  message: string;
  userId: string | null;
  at: number;
}

const REJECTED_MAX = 100;

/** Über der Frist oder aus der Zukunft (Uhr falsch gestellt): nicht mehr verwenden. */
const isStale = (snapshot: SellerSnapshot, now: number): boolean =>
  now - snapshot.savedAt >= SELLER_LIST_MAX_AGE_MS || snapshot.savedAt > now + 60_000;

export async function saveSellerList(
  store: StationStore,
  person: string | null,
  sellers: readonly IntakeSeller[],
  now: number = Date.now(),
): Promise<void> {
  const snapshot: SellerSnapshot = { savedAt: now, userId: person, sellers: [...sellers] };
  await store.setMeta(SELLERS_KEY, snapshot);
}

/**
 * Die gespeicherte Kopie, aber nur für die Person, die sie geladen hat, und nur bis zur Frist. Eine abgelaufene oder fremde
 * Kopie wird dabei gelöscht.
 */
export async function loadSellerList(
  store: StationStore,
  person: string | null,
  now: number = Date.now(),
): Promise<SellerSnapshot | null> {
  const snapshot = await store.getMeta<SellerSnapshot>(SELLERS_KEY);
  if (snapshot === undefined) return null;
  if (isStale(snapshot, now) || snapshot.userId !== person) {
    await store.deleteMeta(SELLERS_KEY);
    return null;
  }
  return snapshot;
}

export async function clearSellerList(store: StationStore): Promise<void> {
  await store.deleteMeta(SELLERS_KEY);
}

export async function listRejected(
  store: StationStore,
  person: string | null,
): Promise<RejectedScan[]> {
  const all = (await store.getMeta<RejectedScan[]>(REJECTED_KEY)) ?? [];
  return all.filter((entry) => entry.userId === person);
}

export async function addRejected(store: StationStore, entry: RejectedScan): Promise<void> {
  const all = (await store.getMeta<RejectedScan[]>(REJECTED_KEY)) ?? [];
  await store.setMeta(REJECTED_KEY, [...all, entry].slice(-REJECTED_MAX));
}

/** Hinweise auf abgelehnte Scans einer Person als gelesen entfernen. */
export async function dismissRejected(store: StationStore, person: string | null): Promise<void> {
  const all = (await store.getMeta<RejectedScan[]>(REJECTED_KEY)) ?? [];
  await store.setMeta(
    REJECTED_KEY,
    all.filter((entry) => entry.userId !== person),
  );
}

/** Namen aller Annahme-Datenbanken dieses Geräts (leer ohne Auflistung der Datenbanken). */
export async function intakeDbNames(): Promise<string[]> {
  try {
    if (typeof indexedDB.databases !== 'function') return [];
    return (await indexedDB.databases()).flatMap((entry) =>
      entry.name?.startsWith(INTAKE_PREFIX) === true ? [entry.name] : [],
    );
  } catch {
    return [];
  }
}

/** Ist die Annahme dieses Basars auf diesem Gerät schon eingerichtet (lokale Datenbank vorhanden)? Legt nichts an. */
export async function intakeExistsOnDevice(eventId: string): Promise<boolean> {
  return (await intakeDbNames()).includes(intakeDbName(eventId));
}

/**
 * Beim Abmelden: Die gespeicherte Verkäuferliste (mit Namen) gehört nicht der nächsten Person am Gerät. Die Warteschlange der
 * Scans bleibt (sie ist schon erfasst und geht unter dem Konto der erfassenden Person hinaus). Wirft nie.
 */
export async function clearIntakeCaches(): Promise<void> {
  generation += 1;
  for (const name of await intakeDbNames()) {
    try {
      const store = await StationStore.open(name);
      try {
        await clearSellerList(store);
      } finally {
        store.close();
      }
    } catch {
      // Speicher gesperrt: beim nächsten Start räumt `purgeExpiredIntakeLists` auf
    }
  }
}

/**
 * Beim Start: Verkäuferlisten über der Frist (oder mit einem Stand aus der Zukunft, z. B. nach einer falschen Uhr) löschen, auch
 * von Basaren, die auf diesem Gerät nicht mehr geöffnet werden. Hinweise auf abgelehnte Scans bleiben, bis sie gelesen sind
 * (sie enthalten nur Code und Meldung). Wirft nie.
 */
export async function purgeExpiredIntakeLists(now: number = Date.now()): Promise<void> {
  for (const name of await intakeDbNames()) {
    try {
      const store = await StationStore.open(name);
      try {
        const snapshot = await store.getMeta<SellerSnapshot>(SELLERS_KEY);
        if (snapshot !== undefined && isStale(snapshot, now)) await clearSellerList(store);
      } finally {
        store.close();
      }
    } catch {
      // nächster Versuch beim nächsten Start
    }
  }
}

/**
 * Die Annahme ist beendet (Server meldet „nicht geöffnet“): Die Verkäuferliste wird gelöscht. Wartende Scans und Hinweise
 * bleiben liegen (nie Daten verlieren); die lokale Datenbank selbst bleibt bestehen.
 */
export async function endIntake(store: StationStore): Promise<void> {
  await clearSellerList(store);
}
